import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import ComponentCard from "../../common/ComponentCard";
import Label from "../Label";
import Input from "../input/InputField";
import Select from "../Select";
import TextArea from "../input/TextArea";
import Switch from "../switch/Switch";
import Button from "../../ui/button/Button";
import axios from "axios";
import { authService } from "../../../services/authService";

interface CheckoutBuilderFormData {
    active: boolean;
    name: string;
    description: string;
    offers: string[];
    thankYouPageUrl: string;
    backRedirectUrl: string;
}

interface CheckoutBuilderFormProps {
    productId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    checkoutId: string;
    offerId?: string;
}

export default function EditCheckoutBuilderForm({ productId, onSuccess, onError, checkoutId, offerId }: CheckoutBuilderFormProps) {
    const [formData, setFormData] = useState<CheckoutBuilderFormData>({
        active: true,
        name: "",
        description: "",
        offers: [],
        thankYouPageUrl: "",
        backRedirectUrl: ""
    });

    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const navigate = useNavigate();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    // Buscar dados do checkout quando o componente for montado
    useEffect(() => {
        const fetchCheckoutData = async () => {
            if (!checkoutId || !productId || !offerId) {
                setFetchLoading(false);
                setApiError("ID do checkout, produto ou oferta não fornecido");
                return;
            }

            setFetchLoading(true);
            try {
                const response = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers/${offerId}/checkouts/${checkoutId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                if (response.status === 200 && response.data) {
                    const checkoutData = response.data;
                    setFormData({
                        active: checkoutData.active ?? true,
                        name: checkoutData.name || "",
                        description: checkoutData.description || "",
                        offers: checkoutData.offers || [],
                        thankYouPageUrl: checkoutData.thankYouPageUrl || "",
                        backRedirectUrl: checkoutData.backRedirectUrl || ""
                    });
                }
            } catch (error: any) {
                console.error("Erro ao buscar dados do checkout:", error);
                setApiError(
                    error.response?.data?.message ||
                    error.response?.statusText ||
                    "Erro ao carregar os dados do checkout. Tente novamente."
                );
            } finally {
                setFetchLoading(false);
            }
        };

        fetchCheckoutData();
    }, [checkoutId, productId, offerId]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Mapeamento de IDs para estrutura do formData
        switch (id) {
            case "inputName":
                setFormData({ ...formData, name: value });
                break;
            case "inputDescription":
                setFormData({ ...formData, description: value });
                break;
            case "inputThankYouPageUrl":
                setFormData({ ...formData, thankYouPageUrl: value });
                break;
            case "inputBackRedirectUrl":
                setFormData({ ...formData, backRedirectUrl: value });
                break;
            default:
                break;
        }
    };

    const handleTextAreaChange = (value: string) => {
        setFormData({ ...formData, description: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.description && value) {
            setErrors({
                ...errors,
                description: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleSwitchChange = (checked: boolean) => {
        setFormData({
            ...formData,
            active: checked
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleGoBack = () => {
        navigate(`/products/edit/${productId}?tab=checkout`);
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.inputName = "O nome da oferta é obrigatório";
        }

        if (!formData.description.trim()) {
            newErrors.description = "A descrição da oferta é obrigatória";
        }

        if (!formData.thankYouPageUrl.trim()) {
            newErrors.thankYouPageUrl = "A URL da página de agradecimento é obrigatória";
        }

        if (!formData.backRedirectUrl.trim()) {
            newErrors.backRedirectUrl = "A URL de redirecionamento é obrigatória";
        }

        setErrors(newErrors);

        // Se não houver erros, retorna true
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar se temos os IDs necessários
        if (!checkoutId || !productId || !offerId) {
            setApiError("ID do checkout, produto ou oferta não fornecido");
            return;
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            // Preparar dados para API
            const offerData = {
                name: formData.name,
                description: formData.description,
                active: formData.active,
                thankYouPageUrl: formData.thankYouPageUrl,
                backRedirectUrl: formData.backRedirectUrl
            };

            // Atualizar oferta existente usando PUT
            const response = await axios.put(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers/${offerId}/checkouts/${checkoutId}`,
                offerData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.status === 200) {
                setApiSuccess("Template de Checkout atualizado com sucesso!");

                // Callback de sucesso se fornecido
                if (onSuccess) {
                    onSuccess(response.data);
                }
            }
        } catch (error: any) {
            console.error("Erro ao atualizar template de checkout:", error);
            setApiError(error.response?.data?.message || "Erro ao atualizar o template de checkout. Tente novamente.");

            // Callback de erro se fornecido
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
                <ComponentCard title="Configurações do seu checkout">
                    <>
                        {apiError && (
                            <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                                {apiError}
                            </div>
                        )}

                        {apiSuccess && (
                            <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                                {apiSuccess}
                            </div>
                        )}

                        {fetchLoading ? (
                            <div className="p-6 flex justify-center items-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                <div>
                                    <Switch
                                        label="Ativo?"
                                        checked={formData.active}
                                        onChange={handleSwitchChange}
                                    />
                                </div>

                                <div className="mt-6">
                                    <Label htmlFor="inputName">Nome do template de checkout *</Label>
                                    <Input
                                        type="text"
                                        id="inputName"
                                        placeholder="Informe o nome do template de checkout"
                                        onChange={handleInputChange}
                                        value={formData.name}
                                        error={!!errors.inputName}
                                        hint={errors.inputName}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Label htmlFor="inputDescription">Descrição do template de checkout</Label>
                                    <TextArea
                                        value={formData.description}
                                        onChange={handleTextAreaChange}
                                        rows={6}
                                        placeholder="Apenas para seu controle interno. Não é visível para o cliente."
                                        error={!!errors.description}
                                        hint={errors.description}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Label>Ofertas para este checkout *</Label>
                                    <Select
                                        options={[]}
                                        placeholder="Selecione uma opção"
                                        onChange={() => { }}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Label htmlFor="inputThankYouPageUrl">Página de obrigado personalizada</Label>
                                    <Input
                                        type="text"
                                        id="inputThankYouPageUrl"
                                        placeholder="https://seu-website.com"
                                        onChange={handleInputChange}
                                        value={formData.thankYouPageUrl}
                                        error={!!errors.thankYouPageUrl}
                                        hint={errors.thankYouPageUrl}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Label htmlFor="inputBackRedirectUrl">URL de backredirect</Label>
                                    <Input
                                        type="text"
                                        id="inputBackRedirectUrl"
                                        placeholder="https://seu-website.com"
                                        onChange={handleInputChange}
                                        value={formData.backRedirectUrl}
                                        error={!!errors.backRedirectUrl}
                                        hint={errors.backRedirectUrl}
                                    />
                                </div>


                                <div className="mt-8 flex justify-between gap-4.5">
                                    <Button
                                        variant="outline"
                                        size="md"
                                        onClick={handleGoBack}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        size="md"
                                        variant="primary"
                                        onClick={() => handleSubmit()}
                                        disabled={loading}
                                    >
                                        {loading ? "Salvando..." : "Salvar"}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </>
                </ComponentCard>
            </div>
        </div>
    );
}
